import { Mol, Reaction as ReactionType } from '@moleculeone/types';

import { useMapReaction, useSmilesToMol } from '../../../hooks';

import { BaseReaction, BaseReactionProps } from './BaseReaction';

export interface ReactionProps extends Omit<BaseReactionProps, 'productMol' | 'substrateMols' | 'highlights'> {
  reaction: ReactionType;
  reactionMols?: Mol[];
  renderDelay?: number;
  loadingComponent?: React.ReactNode;
  onReady?: () => void;
}

export function Reaction(props: ReactionProps) {
  const {
    reaction,
    reactionMols,
    productMap,
    substratesMap,
    renderDelay,
    loadingComponent = null,
    onReady = () => {},
    ...restProps
  } = props;
  const { product, substrates } = reaction;

  const { mols, loading: molsLoading } = useSmilesToMol({
    enabled: !reactionMols,
    smiles: [...substrates, product],
    delay: renderDelay,
    shouldCalculateAtomPositions: false,
  });
  const shouldMap = !productMap && !substratesMap;
  const { reactionWithMapping, loading: reactionLoading } = useMapReaction({
    enabled: shouldMap,
    reaction,
    delay: renderDelay,
  });

  const finalMols = reactionMols ?? mols;
  const finalMapping = shouldMap
    ? reactionWithMapping
    : {
        productMap,
        substratesMap,
      };

  const isReady = Boolean(finalMols) && Boolean(finalMapping);

  if (molsLoading || reactionLoading) {
    return loadingComponent;
  }

  if (!isReady || !finalMols) {
    return null;
  }

  const substrateMols = finalMols.slice(0, -1);
  const productMol = finalMols.slice(-1)[0];

  return (
    <BaseReaction
      {...restProps}
      productMol={productMol}
      substrateMols={substrateMols}
      productMap={finalMapping?.productMap}
      substratesMap={finalMapping?.substratesMap}
      onReady={onReady}
    />
  );
}
